import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter,
  DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { toUserError } from "@/lib/api-error";

interface Empresa {
  id: string;
  nome: string;
  slug: string | null;
  segmento: string | null;
  tipo: string | null;
  cidade: string | null;
  pais?: string | null;
  website: string | null;
  linkedin_url: string | null;
  descricao: string | null;
  metadata: Record<string, unknown> | null;
}

interface Props {
  empresa: Empresa | null;
  onClose: () => void;
  onSaved: () => void;
}

export function EmpresaEditModal({ empresa, onClose, onSaved }: Props) {
  const { toast } = useToast();
  const [form, setForm] = useState<Partial<Empresa>>({});
  const [agenciaId, setAgenciaId] = useState<string | null>(null);

  useEffect(() => {
    if (empresa) {
      setForm({ ...empresa });
      // Busca agencia atualmente linkada
      supabase
        .from("agencias" as never)
        .select("id")
        .eq("empresa_id", empresa.id)
        .maybeSingle()
        .then(({ data }) => setAgenciaId((data as unknown as { id: string } | null)?.id ?? null));
    } else {
      setForm({});
      setAgenciaId(null);
    }
  }, [empresa]);

  // Lista agências disponíveis (pra combobox)
  const { data: agencias = [] } = useQuery({
    queryKey: ["agencias-for-empresa-edit"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("agencias" as never)
        .select("id, nome, empresa_id")
        .order("nome");
      if (error) throw error;
      return (data as unknown as Array<{ id: string; nome: string; empresa_id: string | null }>) ?? [];
    },
    enabled: !!empresa,
  });

  const save = useMutation({
    mutationFn: async () => {
      if (!empresa) return;
      const metadata = { ...(empresa.metadata ?? {}), curated_at: new Date().toISOString() };
      const updates = {
        nome: form.nome,
        segmento: form.segmento || null,
        tipo: form.tipo || null,
        cidade: form.cidade || null,
        pais: form.pais || null,
        website: form.website || null,
        linkedin_url: form.linkedin_url || null,
        descricao: form.descricao || null,
        metadata,
      };
      // Cast pra `any` em runtime — tabelas empresas/agencias.empresa_id ainda
      // não estão refletidas no types.ts gerado do Supabase. Após rodar as
      // migrations #94+ e regenerar supabase types, esse cast pode sair.
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const client = supabase as any;
      const { error } = await client.from("empresas").update(updates).eq("id", empresa.id);
      if (error) throw error;
      await client.from("agencias").update({ empresa_id: null }).eq("empresa_id", empresa.id);
      if (agenciaId) {
        await client.from("agencias").update({ empresa_id: empresa.id }).eq("id", agenciaId);
      }
    },
    onSuccess: onSaved,
    onError: (e: Error) => {
      toast({ title: "Erro ao salvar", description: toUserError(e).description, variant: "destructive" });
    },
  });

  if (!empresa) return null;

  return (
    <Dialog open={!!empresa} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Editar empresa</DialogTitle>
          <DialogDescription>
            Curadoria manual sobrescreve dado existente. Quando uma nova extração via IA encontrar essa empresa, ela <strong>não</strong> sobrescreverá os campos curados.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <Field label="Nome">
            <Input
              value={form.nome ?? ""}
              onChange={(e) => setForm((f) => ({ ...f, nome: e.target.value }))}
            />
          </Field>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tipo">
              <Select
                value={form.tipo ?? "_unset"}
                onValueChange={(v) => setForm((f) => ({ ...f, tipo: v === "_unset" ? null : v }))}
              >
                <SelectTrigger><SelectValue placeholder="Não definido" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_unset">Não definido</SelectItem>
                  <SelectItem value="agencia">Agência</SelectItem>
                  <SelectItem value="cliente">Cliente</SelectItem>
                  <SelectItem value="marca">Marca</SelectItem>
                  <SelectItem value="evento">Evento</SelectItem>
                  <SelectItem value="studio">Studio</SelectItem>
                  <SelectItem value="outro">Outro</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label="Segmento">
              <Input
                value={form.segmento ?? ""}
                placeholder="ex: Agência publicitária"
                onChange={(e) => setForm((f) => ({ ...f, segmento: e.target.value }))}
              />
            </Field>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Cidade">
              <Input
                value={form.cidade ?? ""}
                onChange={(e) => setForm((f) => ({ ...f, cidade: e.target.value }))}
              />
            </Field>
            <Field label="País">
              <Input
                value={form.pais ?? ""}
                placeholder="Brasil"
                onChange={(e) => setForm((f) => ({ ...f, pais: e.target.value }))}
              />
            </Field>
          </div>

          <Field label="Website">
            <Input
              value={form.website ?? ""}
              placeholder="https://..."
              onChange={(e) => setForm((f) => ({ ...f, website: e.target.value }))}
            />
          </Field>

          <Field label="LinkedIn">
            <Input
              value={form.linkedin_url ?? ""}
              placeholder="https://linkedin.com/company/..."
              onChange={(e) => setForm((f) => ({ ...f, linkedin_url: e.target.value }))}
            />
          </Field>

          <Field label="Descrição">
            <Textarea
              value={form.descricao ?? ""}
              rows={2}
              onChange={(e) => setForm((f) => ({ ...f, descricao: e.target.value }))}
            />
          </Field>

          <Field label="Vincular a uma agência cadastrada">
            <Select
              value={agenciaId ?? "_none"}
              onValueChange={(v) => setAgenciaId(v === "_none" ? null : v)}
            >
              <SelectTrigger><SelectValue placeholder="Nenhuma" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="_none">Nenhuma</SelectItem>
                {agencias
                  .filter((a) => !a.empresa_id || a.empresa_id === empresa.id)
                  .map((a) => (
                    <SelectItem key={a.id} value={a.id}>{a.nome}</SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </Field>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={() => save.mutate()} disabled={save.isPending}>
            {save.isPending ? "Salvando..." : "Salvar"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      {children}
    </div>
  );
}
